import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EmpresaConfigService } from '../../../../services/configuracoes/empresa.service';
import {
  FormatoImpressao,
  ImpressaoPedidoService,
} from '../../../../services/pedidos/impressao-pedido.service';

/**
 * Dialog modal para imprimir ou baixar o pedido em PDF (Jasper).
 *
 * Padrao visual `dialog-wave-redesign` (espelhando o dialog de impressao da
 * fatura-view — decisao arquitetural de duplicar em vez de compartilhar).
 *
 * Atalhos de teclado (ativos somente com `visible === true`):
 *  - `Esc` -> cancelar (fecha sem chamar backend).
 *  - `Enter` -> imprimir (abre PDF em nova aba).
 *  - `Ctrl+Shift+D` -> baixar (download forcado).
 *
 * Autorizacao:
 *  - Acesso a rota `/pedidos` ja e garantido pelo guard `gestaoDePedidos`.
 *  - A chave `imprimirPedido` e UI-only (nao existe em `PermissaoEnum`).
 *  - Tenancia granular (pedido de outra empresa -> 404) e autoritativa no backend.
 */
@Component({
  selector: 'app-imprimir-pedido-dialog',
  templateUrl: './imprimir-pedido-dialog.component.html',
  styleUrls: ['./imprimir-pedido-dialog.component.scss'],
})
export class ImprimirPedidoDialogComponent implements OnInit {
  /** Controla a visibilidade do dialog (two-way binding). */
  @Input() visible = false;

  /** Identificador do pedido. Se ausente, os handlers sao no-op. */
  @Input() pedidoId: number | null = null;

  /** Codigo visivel do pedido (usado no nome do arquivo baixado). */
  @Input() codigoDePedido?: string | number;

  @Output() visibleChange = new EventEmitter<boolean>();

  /** FormGroup com o controle `impressao` (default BOBINA_80, ajustado no ngOnInit). */
  tipoDeImpressaoForm!: FormGroup;

  /** Flag de loading/guard contra cliques multiplos em Imprimir/Baixar. */
  isImprimindo = false;

  constructor(
    private fb: FormBuilder,
    private impressaoPedidoService: ImpressaoPedidoService,
    private empresaConfigService: EmpresaConfigService,
    private messageService: MessageService,
    public translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.tipoDeImpressaoForm = this.fb.group({
      impressao: ['BOBINA_80', Validators.required],
    });
    this.carregarFormatoPadraoDaEmpresa();
  }

  // ============================================================
  // Atalhos de teclado
  // ============================================================

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.visible) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelar();
      return;
    }

    // Enter -> Imprimir (desde que o foco nao esteja em input/textarea).
    if (event.key === 'Enter') {
      const alvo = document.activeElement;
      if (alvo && ['INPUT', 'TEXTAREA'].includes(alvo.tagName)) {
        return;
      }
      event.preventDefault();
      this.imprimir();
      return;
    }

    // Ctrl+Shift+D -> Baixar. preventDefault+stopPropagation evita conflito
    // com o shortcut nativo do Chrome (Bookmark Manager).
    if (event.ctrlKey && event.shiftKey && (event.key === 'd' || event.key === 'D')) {
      event.preventDefault();
      event.stopPropagation();
      this.baixar();
      return;
    }
  }

  // ============================================================
  // Acoes principais
  // ============================================================

  imprimir(): void {
    if (this.isImprimindo || !this.pedidoId) return;
    const formato = this.tipoDeImpressaoForm.value?.impressao as FormatoImpressao;
    if (!formato) return;

    this.isImprimindo = true;
    this.impressaoPedidoService.gerarPdfDePedido(this.pedidoId, formato).subscribe({
      next: (pdfBase64: string) => {
        try {
          const blob = this.base64ParaBlob(pdfBase64);
          const url = URL.createObjectURL(blob);
          const janela = window.open(url, '_blank');
          if (!janela || janela.closed) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('pedidos.imprimir_pedido.mensagens.popup_bloqueado'),
            });
          } else {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('pedidos.imprimir_pedido.mensagens.sucesso_impressao'),
            });
          }
          // Revoga apos 60s para nao quebrar o leitor de PDF recem-aberto.
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          this.isImprimindo = false;
          this.fechar();
        } catch {
          this.isImprimindo = false;
          this.toastErroGenerico();
        }
      },
      error: (err) => this.tratarErro(err),
    });
  }

  baixar(): void {
    if (this.isImprimindo || !this.pedidoId) return;
    const formato = this.tipoDeImpressaoForm.value?.impressao as FormatoImpressao;
    if (!formato) return;

    this.isImprimindo = true;
    const nomeArquivo = `pedido-${this.codigoDePedido ?? this.pedidoId}.pdf`;

    this.impressaoPedidoService.gerarPdfDePedido(this.pedidoId, formato).subscribe({
      next: (pdfBase64: string) => {
        try {
          const blob = this.base64ParaBlob(pdfBase64);
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = nomeArquivo;
          a.click();
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('pedidos.imprimir_pedido.mensagens.sucesso_download'),
          });
          this.isImprimindo = false;
          this.fechar();
        } catch {
          this.isImprimindo = false;
          this.toastErroGenerico();
        }
      },
      error: (err) => this.tratarErro(err),
    });
  }

  cancelar(): void {
    this.fechar();
  }

  // ============================================================
  // Helpers privados
  // ============================================================

  /**
   * Carrega o formato padrao da empresa via `EmpresaConfigService.get`.
   * Espelha o padrao `fatura-view.carregarMoedaPadrao` (linha 575).
   *
   * Fallback: mantem `BOBINA_80` definido no FormBuilder quando a empresa
   * nao tem `tipoDeFormatoDaImpressao` configurado ou quando a request falha.
   */
  private carregarFormatoPadraoDaEmpresa(): void {
    const idEmpresa = sessionStorage.getItem('id') || '';
    if (!idEmpresa) return;

    this.empresaConfigService.get(`/configuracaoEmpresa/${idEmpresa}`).subscribe({
      next: (res: any) => {
        const tipoImpressaoEmpresa = res?.tipoDeFormatoDaImpressao;
        if (!tipoImpressaoEmpresa) return;

        const valor = typeof tipoImpressaoEmpresa === 'string'
          ? tipoImpressaoEmpresa
          : tipoImpressaoEmpresa?.enum;

        if (valor) {
          this.tipoDeImpressaoForm.patchValue({ impressao: valor });
        }
      },
      error: () => {
        // Silencioso: mantem default BOBINA_80 do FormBuilder.
      },
    });
  }

  /**
   * Decodifica uma string Base64 em um Blob `application/pdf`.
   * Espelha `fatura-view.openPdfFromBase64` (linha 1720).
   *
   * @throws `InvalidCharacterError` se o Base64 for malformado — capturado
   *         pelos callers via try/catch e convertido em toast generico.
   */
  private base64ParaBlob(base64: string): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: 'application/pdf' });
  }

  /**
   * Tratamento centralizado de erros HTTP do endpoint Jasper.
   * - 404 -> pedido inexistente OU tenancia negou (outra empresa).
   * - Demais (5xx/timeout/401/403 residual) -> erro generico.
   * Em qualquer erro o dialog fecha para evitar retentativas infrutiferas.
   */
  private tratarErro(err: any): void {
    this.isImprimindo = false;
    const status = err?.status;
    const chave = status === 404
      ? 'pedidos.imprimir_pedido.mensagens.erro_pedido_nao_encontrado'
      : 'pedidos.imprimir_pedido.mensagens.erro_gerar_pedido';
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant(chave),
    });
    this.fechar();
  }

  private toastErroGenerico(): void {
    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('pedidos.imprimir_pedido.mensagens.erro_gerar_pedido'),
    });
    this.fechar();
  }

  private fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
